/**
 * SCORM Service
 *
 * Handles SCORM-related API calls including manifest parsing
 */

import axiosInstance from './httpClient';

export interface ScormManifest {
  entryPoint: string;
  scormVersion: string;
  title: string;
  packagePath?: string;
  manifestPath?: string;
  fallback?: boolean;
}

export interface ScormManifestResponse {
  success: boolean;
  data: ScormManifest;
  message?: string;
}

/**
 * Fetch SCORM manifest information for a package
 *
 * @param packageName - The SCORM package folder name (e.g., "1770882336515")
 * @returns Promise with manifest data including entry point
 */
export const fetchScormManifest = async (packageName: string): Promise<ScormManifest> => {
  try {
    const response = await axiosInstance.get(
      `/scorm-manifest/${packageName}`
    );
    const data = response.data as ScormManifestResponse;

    if (data.success) {
      return data.data;
    }

    throw new Error(data.message || 'Failed to fetch SCORM manifest');
  } catch (error: any) {
    console.error('Error fetching SCORM manifest:', error);

    // Fallback to story.html if manifest fetch fails
    return {
      entryPoint: 'story.html',
      scormVersion: '1.2',
      title: 'SCORM Content',
      fallback: true
    };
  }
};

/**
 * Get SCORM content URL with dynamic entry point
 *
 * @param packageName - The SCORM package folder name
 * @param baseUrl - The base URL for lessons (from assetUrl helper)
 * @returns Promise with full SCORM content URL
 */
export const getScormContentUrl = async (
  packageName: string,
  baseUrl: string
): Promise<string> => {
  const manifest = await fetchScormManifest(packageName);
  return `${baseUrl}/scorm_packages/${packageName}/${manifest.entryPoint}`;
};

export default {
  fetchScormManifest,
  getScormContentUrl
};
